import React, { useState, useRef, useEffect } from 'react';
import { useAuth } from '../contexts/AuthContext';
import { UserIcon, ChevronDownIcon, LogoutIcon, LoadingIcon } from './Icons';

interface UserDropdownProps {
  className?: string;
  func?: () => void;
}

export const UserDropdown: React.FC<UserDropdownProps> = ({ className = '', func }) => {
  const { user, logout } = useAuth();
  const [isOpen, setIsOpen] = useState(false);
  const [isLoggingOut, setIsLoggingOut] = useState(false);
  const dropdownRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target as Node)) {
        setIsOpen(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, []);

  if (!user) return null;

  const handleLogout = async () => {
    setIsLoggingOut(true);
    setIsOpen(false);

    // Faz logout imediatamente
    logout();

    // Pequeno delay apenas para feedback visual (não bloqueia funcionalidade)
    setTimeout(() => {
      setIsLoggingOut(false);
    }, 300);
  };

  return (
    <div className={`relative ${className}`} ref={dropdownRef}>
      {/* Botão do usuário */}
      <button
        onClick={() => setIsOpen(!isOpen)}
        className="flex items-center space-x-2 px-3 py-2 bg-white border border-gray-200 rounded-lg shadow-sm hover:bg-gray-50 hover:shadow-md transition-all duration-200 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-2"
        title="Menu do usuário"
        aria-label="Abrir menu do usuário"
      >
        <UserIcon size={20} className="text-gray-600" />
        <ChevronDownIcon
          size={16}
          className={`text-gray-400 transition-transform duration-200 ${isOpen ? 'rotate-180' : ''}`}
        />
      </button>

      {/* Dropdown Menu */}
      {isOpen && (
        <div className="absolute right-0 mt-2 w-56 bg-white border border-gray-200 rounded-lg shadow-lg z-50 overflow-hidden">
          {/* Header do usuário */}
          <div className="px-4 py-3 bg-gray-50 border-b border-gray-200">
            <div className="flex items-center space-x-3">
              <div className="flex-shrink-0">
                <div className="w-8 h-8 bg-blue-500 rounded-full flex items-center justify-center">
                  <UserIcon size={16} className="text-white" />
                </div>
              </div>
              <div className="flex-1 min-w-0">
                <p className="text-sm font-medium text-gray-900 truncate">
                  {user.notificador || user.documento}
                </p>
                <p className="text-xs text-gray-500 truncate">
                  {user.documento}
                </p>
              </div>
            </div>
          </div>

          {/* Menu Items */}
          <div className="">
            <button
              className="w-full text-left px-4 py-4 text-sm text-gray-700 hover:bg-gray-100 hover:text-gray-900 transition-colors duration-150 cursor-pointer"
              onClick={() => func && func()}
            >
              <p>Atualizar endereços</p>
            </button>
            <div className="border-t border-gray-100"></div>

            {/* Botão de logout */}
            <button
              onClick={handleLogout}
              disabled={isLoggingOut}
              className="w-full text-left px-4 py-2 text-sm text-red-600 hover:bg-red-50 hover:text-red-700 transition-colors duration-150 flex items-center space-x-2 disabled:opacity-50 disabled:cursor-not-allowed"
            >
              {isLoggingOut ? (
                <LoadingIcon size={16} className="text-red-500" />
              ) : (
                <LogoutIcon size={16} className="text-red-500" />
              )}
              <span>{isLoggingOut ? 'Saindo...' : 'Sair'}</span>
            </button>
          </div>
        </div>
      )}
    </div>
  );
};

export default UserDropdown;
